import React, { useState, useEffect } from 'react';
import { 
  Search, TrendingUp, AlertCircle, ExternalLink, Plus, Eye,
  Target, BarChart3, PieChart, Activity, Zap, Clock,
  RefreshCw, Download, Filter, Star, Bell, Settings,
  GitCompare, DollarSign, Award, Users, CheckCircle
} from 'lucide-react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell,
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Competitor Profile Card
function CompetitorCard({ competitor, onView, onTrack, isTracked }) {
  return (
    <div 
      className="card"
      style={{
        border: isTracked ? '2px solid var(--accent-500)' : '1px solid var(--border-light)',
        position: 'relative'
      }}
      data-testid={`competitor-card-${competitor.name}`}
    >
      {isTracked && (
        <div style={{
          position: 'absolute',
          top: '-8px',
          right: '16px',
          background: 'var(--accent-500)',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <Bell size={12} />
          Tracking
        </div>
      )}

      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, var(--primary-500), var(--accent-500))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '1.5rem',
          fontWeight: '700'
        }}>
          {competitor.name.charAt(0)}
        </div>

        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '6px' }}>
            {competitor.name}
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
            {competitor.industry || 'Technology'} • {competitor.activity_level || 'High'} Activity
          </p>

          <div className="grid grid-cols-3" style={{ gap: '8px', marginBottom: '12px' }}>
            <div style={{ padding: '8px', background: 'var(--bg-secondary)', borderRadius: '6px', textAlign: 'center' }}>
              <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)' }}>Mentions</p>
              <p style={{ fontSize: '1.125rem', fontWeight: '700' }}>{competitor.recent_mentions || 5}</p>
            </div>
            <div style={{ padding: '8px', background: 'var(--bg-secondary)', borderRadius: '6px', textAlign: 'center' }}>
              <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)' }}>Est. Spend</p>
              <p style={{ fontSize: '0.875rem', fontWeight: '700' }}>{competitor.estimated_spend || '$100K+'}</p>
            </div>
            <div style={{ padding: '8px', background: 'var(--bg-secondary)', borderRadius: '6px', textAlign: 'center' }}>
              <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)' }}>Score</p>
              <p style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--warning-600)' }}>8.5</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => onView(competitor)}
              className="btn btn-primary"
              style={{ flex: 1, fontSize: '0.875rem', padding: '8px' }}
            >
              <Eye size={16} />
              View Details
            </button>
            <button
              onClick={() => onTrack(competitor)}
              className={isTracked ? 'btn btn-secondary' : 'btn'}
              style={{ 
                fontSize: '0.875rem', 
                padding: '8px 12px',
                background: isTracked ? 'var(--bg-tertiary)' : 'var(--accent-500)',
                color: isTracked ? 'var(--text-primary)' : 'white',
                border: 'none'
              }}
            >
              <Star size={16} fill={isTracked ? 'var(--warning-500)' : 'none'} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Competitive Positioning Map
function PositioningMap({ competitors }) {
  const data = competitors.map((comp, idx) => ({
    name: comp.name,
    quality: 70 + Math.random() * 25,
    price: 60 + Math.random() * 30,
    size: 80 + Math.random() * 40
  }));

  // Add "You" as reference point
  data.push({ name: 'You (TechCo)', quality: 85, price: 75, size: 100, isYou: true });

  return (
    <div style={{ height: '400px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
          <XAxis 
            type="number" 
            dataKey="price" 
            name="Price" 
            label={{ value: 'Price Point →', position: 'insideBottom', offset: -15, style: { fontSize: '12px', fontWeight: '600' } }}
            domain={[50, 100]}
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            type="number" 
            dataKey="quality" 
            name="Quality" 
            label={{ value: 'Quality Score →', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fontWeight: '600' } }}
            domain={[60, 100]}
            style={{ fontSize: '12px' }}
          />
          <ZAxis type="number" dataKey="size" range={[100, 400]} name="Market Presence" />
          <Tooltip 
            cursor={{ strokeDasharray: '3 3' }}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return (
                  <div style={{ 
                    background: 'white', 
                    padding: '12px', 
                    border: '2px solid ' + (data.isYou ? 'var(--accent-500)' : 'var(--border-medium)'),
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    <p style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '6px', color: data.isYou ? 'var(--accent-600)' : 'inherit' }}>
                      {data.name}
                    </p>
                    <p style={{ fontSize: '0.75rem' }}>Quality: {data.quality.toFixed(0)}</p>
                    <p style={{ fontSize: '0.75rem' }}>Price: {data.price.toFixed(0)}</p>
                    <p style={{ fontSize: '0.75rem' }}>Market Presence: {data.size.toFixed(0)}</p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Scatter 
            data={data.filter(d => !d.isYou)} 
            fill="var(--primary-500)"
          />
          <Scatter 
            data={data.filter(d => d.isYou)} 
            fill="var(--accent-500)"
            shape="star"
          />
        </ScatterChart>
      </ResponsiveContainer>
      
      {/* Legend */}
      <div style={{ marginTop: '16px', display: 'flex', gap: '24px', justifyContent: 'center', fontSize: '0.875rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: 'var(--primary-500)' }}></div>
          <span>Competitors</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', background: 'var(--accent-500)' }}></div>
          <span>Your Position</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Bubble size = Market Presence</span>
        </div>
      </div>
    </div>
  );
}

// Market Share Chart
function MarketShareChart({ competitors }) {
  const data = competitors.map((comp, idx) => ({
    name: comp.name,
    share: 15 + Math.random() * 20
  }));
  
  // Add "You"
  data.push({ name: 'You (TechCo)', share: 18 });
  
  const COLORS = ['#3B82F6', '#14B8A6', '#F59E0B', '#8B5CF6', '#EC4899', '#10B981'];

  return (
    <div style={{ height: '350px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPie>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={true}
            label={(entry) => `${entry.name}: ${entry.share.toFixed(1)}%`}
            outerRadius={120}
            fill="#8884d8"
            dataKey="share"
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.name.includes('You') ? 'var(--accent-500)' : COLORS[index % COLORS.length]} 
              />
            ))}
          </Pie>
          <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
        </RechartsPie>
      </ResponsiveContainer>
    </div>
  );
}

// Competitive Trends Chart
function CompetitiveTrendsChart({ trendData }) {
  const data = [
    { month: 'Jan', you: 12, competitor1: 15, competitor2: 10, competitor3: 8 },
    { month: 'Feb', you: 14, competitor1: 16, competitor2: 11, competitor3: 9 },
    { month: 'Mar', you: 16, competitor1: 17, competitor2: 12, competitor3: 10 },
    { month: 'Apr', you: 18, competitor1: 18, competitor2: 13, competitor3: 11 },
    { month: 'May', you: 20, competitor1: 19, competitor2: 14, competitor3: 12 },
    { month: 'Jun', you: 22, competitor1: 20, competitor2: 15, competitor3: 13 }
  ];

  return (
    <div style={{ height: '300px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
          <XAxis dataKey="month" style={{ fontSize: '12px' }} />
          <YAxis 
            label={{ value: 'Activity Score', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
            style={{ fontSize: '12px' }}
          />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="you" stroke="var(--accent-500)" strokeWidth={3} name="You" />
          <Line type="monotone" dataKey="competitor1" stroke="#3B82F6" strokeWidth={2} name="Salesforce" />
          <Line type="monotone" dataKey="competitor2" stroke="#10B981" strokeWidth={2} name="HubSpot" />
          <Line type="monotone" dataKey="competitor3" stroke="#F59E0B" strokeWidth={2} name="Adobe" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Competitor Detail Modal
function CompetitorDetailModal({ competitor, searchResults, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!competitor) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '20px',
          width: '95%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="competitor-detail-modal"
      >
        {/* Header */}
        <div style={{ 
          padding: '24px 32px', 
          borderBottom: '2px solid var(--border-light)',
          background: 'linear-gradient(135deg, hsl(210, 100%, 98%) 0%, white 100%)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px' }}>
                {competitor.name}
              </h2>
              <div style={{ display: 'flex', gap: '12px' }}>
                <span className="badge badge-info">{competitor.industry || 'Technology'}</span>
                <span className="badge badge-warning">Activity: {competitor.activity_level || 'High'}</span>
                <span className="badge" style={{ background: 'var(--bg-tertiary)' }}>Score: 8.5/10</span>
              </div>
            </div>
            <button onClick={onClose} className="btn btn-secondary">
              ✕ Close
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: '2px solid var(--border-light)', padding: '0 32px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'activity', label: 'Recent Activity', icon: Activity },
              { id: 'insights', label: 'AI Insights', icon: Zap }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '14px 20px',
                    border: 'none',
                    background: activeTab === tab.id ? 'hsl(210, 100%, 98%)' : 'transparent',
                    borderBottom: activeTab === tab.id ? '3px solid var(--primary-500)' : '3px solid transparent',
                    color: activeTab === tab.id ? 'var(--primary-600)' : 'var(--text-secondary)',
                    fontWeight: activeTab === tab.id ? '700' : '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '0.875rem'
                  }}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '32px' }}>
          {activeTab === 'overview' && (
            <div>
              {/* Metrics Grid */}
              <div className="grid grid-cols-4" style={{ gap: '16px', marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '12px', textAlign: 'center' }}>
                  <Activity size={24} style={{ margin: '0 auto 8px', color: 'var(--primary-500)' }} />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Recent Mentions</p>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>{competitor.recent_mentions || 5}</p>
                </div>
                <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '12px', textAlign: 'center' }}>
                  <DollarSign size={24} style={{ margin: '0 auto 8px', color: 'var(--success-500)' }} />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Est. Ad Spend</p>
                  <p style={{ fontSize: '1.25rem', fontWeight: '700' }}>{competitor.estimated_spend || '$100K+'}</p>
                </div>
                <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '12px', textAlign: 'center' }}>
                  <Users size={24} style={{ margin: '0 auto 8px', color: 'var(--accent-500)' }} />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Market Share</p>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>18%</p>
                </div>
                <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '12px', textAlign: 'center' }}>
                  <Award size={24} style={{ margin: '0 auto 8px', color: 'var(--warning-500)' }} />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Competitive Score</p>
                  <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>8.5</p>
                </div>
              </div>

              {/* Strengths & Weaknesses */}
              <div className="grid grid-cols-2" style={{ gap: '24px' }}>
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--success-600)' }}>
                    <CheckCircle size={20} />
                    Strengths
                  </h3>
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <li style={{ padding: '10px', background: 'hsl(142, 76%, 96%)', borderRadius: '6px', fontSize: '0.875rem' }}>
                      ✓ Strong brand recognition in enterprise market
                    </li>
                    <li style={{ padding: '10px', background: 'hsl(142, 76%, 96%)', borderRadius: '6px', fontSize: '0.875rem' }}>
                      ✓ Comprehensive product suite
                    </li>
                    <li style={{ padding: '10px', background: 'hsl(142, 76%, 96%)', borderRadius: '6px', fontSize: '0.875rem' }}>
                      ✓ Active social media presence
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--warning-600)' }}>
                    <AlertCircle size={20} />
                    Opportunities
                  </h3>
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <li style={{ padding: '10px', background: 'hsl(38, 92%, 95%)', borderRadius: '6px', fontSize: '0.875rem' }}>
                      ⚠ Limited video content marketing
                    </li>
                    <li style={{ padding: '10px', background: 'hsl(38, 92%, 95%)', borderRadius: '6px', fontSize: '0.875rem' }}>
                      ⚠ Pricing perception challenges
                    </li>
                    <li style={{ padding: '10px', background: 'hsl(38, 92%, 95%)', borderRadius: '6px', fontSize: '0.875rem' }}>
                      ⚠ Slower innovation cycle
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '16px' }}>Recent Activity</h3>
              {searchResults && searchResults.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {searchResults.map((result, idx) => (
                    <div key={idx} style={{ 
                      padding: '16px', 
                      background: 'var(--bg-tertiary)', 
                      borderRadius: '12px',
                      borderLeft: '4px solid var(--primary-500)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                        <a 
                          href={result.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            fontSize: '0.9375rem', 
                            fontWeight: '700', 
                            color: 'var(--primary-600)',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flex: 1
                          }}
                        >
                          {result.title}
                          <ExternalLink size={14} />
                        </a>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          padding: '4px 10px', 
                          background: 'var(--success-500)', 
                          color: 'white',
                          borderRadius: '6px',
                          fontWeight: '700'
                        }}>
                          {(result.score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                        {result.content}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <Search size={48} className="empty-state-icon" />
                  <p>No recent activity data available</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'insights' && (
            <div>
              <div style={{ 
                padding: '24px', 
                background: 'hsl(180, 65%, 97%)', 
                border: '2px solid var(--accent-500)',
                borderRadius: '12px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '12px', 
                    background: 'var(--accent-500)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Zap size={24} />
                  </div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--accent-700)' }}>
                    AI-Generated Competitive Insights
                  </h3>
                </div>
                <p style={{ fontSize: '0.9375rem', lineHeight: '1.7', color: 'var(--text-primary)' }}>
                  {competitor.name} is demonstrating increased activity in video marketing with a focus on AI-powered features. 
                  Their estimated ad spend has grown 15% quarter-over-quarter, primarily allocated to search and social channels. 
                  Market positioning suggests premium pricing with emphasis on enterprise customers.
                </p>
              </div>

              <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '16px' }}>Key Findings</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { icon: '📈', title: 'Ad Spend Trend', value: '+15% QoQ', sentiment: 'up' },
                  { icon: '🎯', title: 'Primary Channels', value: 'Search (45%), Social (35%)', sentiment: 'neutral' },
                  { icon: '👥', title: 'Target Audience', value: 'Enterprise IT Decision Makers', sentiment: 'neutral' },
                  { icon: '💰', title: 'Pricing Strategy', value: 'Premium positioning', sentiment: 'neutral' },
                  { icon: '🚀', title: 'Innovation Focus', value: 'AI & Automation features', sentiment: 'up' }
                ].map((finding, idx) => (
                  <div key={idx} style={{ 
                    padding: '16px', 
                    background: 'white',
                    border: '1px solid var(--border-light)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}>
                    <div style={{ fontSize: '2rem' }}>{finding.icon}</div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '4px' }}>{finding.title}</p>
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{finding.value}</p>
                    </div>
                    {finding.sentiment === 'up' && (
                      <TrendingUp size={20} style={{ color: 'var(--success-500)' }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main Competitive Intelligence Page
function CompetitiveIntelligence() {
  const [competitors, setCompetitors] = useState('');
  const [industry, setIndustry] = useState('technology');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trackedCompetitors, setTrackedCompetitors] = useState([
    { name: 'Salesforce', recent_mentions: 5, estimated_spend: '$100K+', activity_level: 'High', industry: 'Technology' },
    { name: 'HubSpot', recent_mentions: 4, estimated_spend: '$80K-$100K', activity_level: 'High', industry: 'Technology' },
    { name: 'Adobe', recent_mentions: 3, estimated_spend: '$75K-$100K', activity_level: 'Medium', industry: 'Technology' }
  ]);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');

  const searchCompetitors = async () => {
    if (!competitors.trim()) return;

    try {
      setLoading(true);
      const competitorList = competitors.split(',').map(c => c.trim()).filter(Boolean);
      
      const response = await axios.post(`${API_BASE_URL}/api/competitive/search-competitors`, {
        competitors: competitorList,
        industry: industry
      });
      
      setResults(response.data);
      
      // Add to tracked competitors if not already there
      competitorList.forEach(comp => {
        if (!trackedCompetitors.find(t => t.name.toLowerCase() === comp.toLowerCase())) {
          setTrackedCompetitors(prev => [...prev, {
            name: comp,
            recent_mentions: response.data.competitor_results?.[comp]?.length || 0,
            estimated_spend: '$50K-$100K',
            activity_level: response.data.competitor_results?.[comp]?.length > 3 ? 'High' : 'Medium',
            industry: industry
          }]);
        }
      });
    } catch (error) {
      console.error('Error searching competitors:', error);
      alert('Error searching. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCompetitor = (competitor) => {
    setSelectedCompetitor(competitor);
    setShowDetailModal(true);
  };

  const handleTrackCompetitor = (competitor) => {
    const isTracked = trackedCompetitors.some(t => t.name === competitor.name);
    if (isTracked) {
      setTrackedCompetitors(trackedCompetitors.filter(t => t.name !== competitor.name));
    } else {
      setTrackedCompetitors([...trackedCompetitors, competitor]);
    }
  };

  const views = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'positioning', label: 'Positioning Map', icon: Target },
    { id: 'market-share', label: 'Market Share', icon: PieChart },
    { id: 'trends', label: 'Trends', icon: TrendingUp },
    { id: 'search', label: 'Search', icon: Search }
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Competitive Intelligence</h1>
        <p className="page-subtitle">Real-time competitor monitoring with AI-powered market insights</p>
      </div>

      {/* View Selector */}
      <div style={{ marginBottom: '24px', borderBottom: '2px solid var(--border-light)' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {views.map(view => {
            const Icon = view.icon;
            return (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  background: activeView === view.id ? 'hsl(210, 100%, 98%)' : 'transparent',
                  borderBottom: activeView === view.id ? '3px solid var(--primary-500)' : '3px solid transparent',
                  color: activeView === view.id ? 'var(--primary-600)' : 'var(--text-secondary)',
                  fontWeight: activeView === view.id ? '700' : '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.875rem'
                }}
                data-testid={`view-${view.id}`}
              >
                <Icon size={16} />
                {view.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dashboard View */}
      {activeView === 'dashboard' && (
        <div>
          {/* Stats Row */}
          <div className="grid grid-cols-4" style={{ gap: '16px', marginBottom: '24px' }}>
            <div className="card">
              <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Tracked Competitors</p>
              <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--primary-600)' }}>{trackedCompetitors.length}</p>
            </div>
            <div className="card">
              <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Total Mentions</p>
              <p style={{ fontSize: '2rem', fontWeight: '700' }}>
                {trackedCompetitors.reduce((sum, c) => sum + (c.recent_mentions || 0), 0)}
              </p>
            </div>
            <div className="card">
              <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>High Activity</p>
              <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--warning-600)' }}>
                {trackedCompetitors.filter(c => c.activity_level === 'High').length}
              </p>
            </div>
            <div className="card">
              <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Avg Comp Score</p>
              <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--accent-600)' }}>8.2</p>
            </div>
          </div>

          {/* Tracked Competitors */}
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '16px' }}>Tracked Competitors</h3>
          <div className="grid grid-cols-2" style={{ gap: '16px', marginBottom: '32px' }}>
            {trackedCompetitors.map((comp, idx) => (
              <CompetitorCard
                key={idx}
                competitor={comp}
                onView={handleViewCompetitor}
                onTrack={handleTrackCompetitor}
                isTracked={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Positioning Map View */}
      {activeView === 'positioning' && (
        <div className="card">
          <h3 className="card-title">Competitive Positioning Map</h3>
          <p className="card-subtitle">Quality vs Price analysis with market presence (bubble size)</p>
          <div style={{ marginTop: '24px' }}>
            <PositioningMap competitors={trackedCompetitors} />
          </div>
          <div style={{ marginTop: '24px', padding: '16px', background: 'hsl(180, 65%, 97%)', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '0.9375rem', fontWeight: '700', marginBottom: '8px', color: 'var(--accent-700)' }}>
              💡 Positioning Insights
            </h4>
            <ul style={{ fontSize: '0.875rem', lineHeight: '1.7', color: 'var(--text-primary)' }}>
              <li>• Your position (teal star) is in the high-quality, mid-to-high price quadrant</li>
              <li>• Salesforce occupies premium positioning (top-right)</li>
              <li>• Opportunity exists to differentiate on value proposition</li>
            </ul>
          </div>
        </div>
      )}

      {/* Market Share View */}
      {activeView === 'market-share' && (
        <div className="card">
          <h3 className="card-title">Market Share Analysis</h3>
          <p className="card-subtitle">Estimated market share by ad spend and presence</p>
          <div style={{ marginTop: '24px' }}>
            <MarketShareChart competitors={trackedCompetitors} />
          </div>
          <div style={{ marginTop: '24px' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '12px' }}>Market Share Breakdown</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {trackedCompetitors.concat([{ name: 'You (TechCo)', share: 18 }]).map((comp, idx) => {
                const share = comp.share || 15 + Math.random() * 10;
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '150px', fontSize: '0.875rem', fontWeight: '600' }}>
                      {comp.name}
                    </div>
                    <div style={{ flex: 1, height: '32px', background: 'var(--bg-tertiary)', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
                      <div style={{ 
                        width: `${share}%`, 
                        height: '100%', 
                        background: comp.name.includes('You') ? 'var(--accent-500)' : 'var(--primary-500)',
                        transition: 'width 0.5s ease'
                      }}></div>
                      <span style={{ 
                        position: 'absolute', 
                        left: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        fontSize: '0.875rem',
                        fontWeight: '700',
                        color: share > 20 ? 'white' : 'var(--text-primary)'
                      }}>
                        {share.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Trends View */}
      {activeView === 'trends' && (
        <div className="card">
          <h3 className="card-title">Competitive Activity Trends</h3>
          <p className="card-subtitle">6-month campaign activity comparison</p>
          <div style={{ marginTop: '24px' }}>
            <CompetitiveTrendsChart />
          </div>
          <div style={{ marginTop: '24px' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '12px' }}>Trend Analysis</h4>
            <div className="grid grid-cols-3" style={{ gap: '16px' }}>
              <div style={{ padding: '16px', background: 'hsl(142, 76%, 96%)', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <TrendingUp size={20} style={{ color: 'var(--success-600)' }} />
                  <p style={{ fontSize: '0.875rem', fontWeight: '700', color: 'var(--success-700)' }}>Your Growth</p>
                </div>
                <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success-700)' }}>+83%</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--success-700)' }}>Jan to Jun 2024</p>
              </div>
              <div style={{ padding: '16px', background: 'hsl(199, 89%, 96%)', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Activity size={20} style={{ color: 'var(--info-600)' }} />
                  <p style={{ fontSize: '0.875rem', fontWeight: '700', color: 'var(--info-700)' }}>Market Avg</p>
                </div>
                <p style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--info-700)' }}>+48%</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--info-700)' }}>Industry average</p>
              </div>
              <div style={{ padding: '16px', background: 'hsl(38, 92%, 95%)', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Target size={20} style={{ color: 'var(--warning-600)' }} />
                  <p style={{ fontSize: '0.875rem', fontWeight: '700', color: 'var(--warning-700)' }}>Top Competitor</p>
                </div>
                <p style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--warning-700)' }}>Salesforce</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--warning-700)' }}>+67% growth</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search View */}
      {activeView === 'search' && (
        <div>
          {/* Search Input */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h3 className="card-title">Search Competitors</h3>
            <div style={{ marginTop: '16px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  Competitor Names (comma-separated)
                </label>
                <input
                  type="text"
                  value={competitors}
                  onChange={(e) => setCompetitors(e.target.value)}
                  placeholder="e.g., Salesforce, HubSpot, Adobe, Microsoft"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '0.875rem'
                  }}
                  data-testid="competitor-input"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
                  Industry
                </label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    background: 'white',
                    width: '300px'
                  }}
                >
                  <option value="technology">Technology</option>
                  <option value="retail">Retail</option>
                  <option value="finance">Finance</option>
                  <option value="healthcare">Healthcare</option>
                </select>
              </div>

              <button
                onClick={searchCompetitors}
                className="btn btn-primary"
                disabled={loading || !competitors.trim()}
                data-testid="search-competitors-btn"
                style={{ fontSize: '0.9375rem', padding: '12px 24px', fontWeight: '700' }}
              >
                <Search size={20} />
                {loading ? 'Searching Web...' : 'Search Competitors (Live AI Search)'}
              </button>
              
              <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '12px' }}>
                🔍 Using Tavily AI-powered search to analyze real-time competitor activity
              </p>
            </div>
          </div>

          {/* Search Results */}
          {loading && (
            <div className="card">
              <div className="loading">
                <div className="spinner"></div>
                <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>
                  Searching the web for competitor intelligence...
                </p>
              </div>
            </div>
          )}

          {!loading && results && (
            <div>
              <div className="card" style={{ marginBottom: '24px', background: 'hsl(180, 65%, 97%)', border: '2px solid var(--accent-500)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <TrendingUp size={24} style={{ color: 'var(--accent-600)' }} />
                  <div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--accent-700)' }}>
                      Search Completed: {results.total_competitors} Competitors Analyzed
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--accent-700)' }}>
                      Real-time web search powered by Tavily AI
                    </p>
                  </div>
                </div>
              </div>

              {/* Competitor Results */}
              {Object.entries(results.competitor_results || {}).map(([competitor, searchResults], idx) => (
                <div key={idx} className="card" style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 className="card-title">{competitor}</h3>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <span className="badge" style={{ background: 'var(--accent-500)', color: 'white' }}>
                        {searchResults.length} mentions
                      </span>
                      <span className="badge badge-warning">
                        {searchResults.length > 3 ? 'High' : 'Medium'} Activity
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3" style={{ gap: '12px', marginTop: '16px', marginBottom: '16px' }}>
                    <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Mentions Found</p>
                      <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>{searchResults.length}</p>
                    </div>
                    <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Activity Level</p>
                      <p style={{ fontSize: '1.125rem', fontWeight: '700', textTransform: 'capitalize' }}>
                        {searchResults.length > 3 ? 'High' : 'Medium'}
                      </p>
                    </div>
                    <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Est. Spend</p>
                      <p style={{ fontSize: '1.125rem', fontWeight: '700' }}>
                        {searchResults.length > 3 ? '$100K+' : '$50K-$100K'}
                      </p>
                    </div>
                  </div>

                  {/* Search Results */}
                  <div>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '12px', color: 'var(--text-secondary)' }}>
                      Recent Activity
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {searchResults.slice(0, 3).map((result, ridx) => (
                        <div key={ridx} style={{ 
                          padding: '12px', 
                          background: 'var(--bg-tertiary)', 
                          borderRadius: '8px',
                          borderLeft: '4px solid var(--primary-500)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                            <a 
                              href={result.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                fontSize: '0.875rem', 
                                fontWeight: '700', 
                                color: 'var(--primary-600)',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              {result.title}
                              <ExternalLink size={14} />
                            </a>
                            <span style={{ 
                              fontSize: '0.75rem', 
                              padding: '2px 8px', 
                              background: 'var(--success-500)', 
                              color: 'white',
                              borderRadius: '4px',
                              fontWeight: '700'
                            }}>
                              {(result.score * 100).toFixed(0)}%
                            </span>
                          </div>
                          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                            {result.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && !results && activeView === 'search' && (
            <div className="card">
              <div className="empty-state">
                <Search size={64} className="empty-state-icon" />
                <p style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '8px' }}>
                  Enter competitor names to begin
                </p>
                <p>Get real-time intelligence on competitor campaigns, pricing, and market activity</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Competitor Detail Modal */}
      {showDetailModal && selectedCompetitor && (
        <CompetitorDetailModal
          competitor={selectedCompetitor}
          searchResults={results?.competitor_results?.[selectedCompetitor.name] || []}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </div>
  );
}

export default CompetitiveIntelligence;
